/* Cognito's exceptions, said in words a rep can act on.
 *
 * One table rather than one per screen. Sign-in and password reset raise an
 * overlapping set, and two copies of the same sentence drift apart the first
 * time one of them is corrected.
 *
 * The context argument exists for the exceptions whose meaning depends on
 * where they were raised. NotAuthorizedException is a wrong password on
 * sign-in and an account still on its first temporary password on a reset,
 * and those two need opposite instructions.
 */

export type AuthContext = "signin" | "reset";

export function authProblem(err: unknown, where: AuthContext): string {
  const name = (err as { name?: string })?.name ?? "";
  const message = (err as { message?: string })?.message ?? "";

  switch (name) {
    case "UserNotFoundException":
      return "There is no account for that email. Ask James to create one.";

    case "NotAuthorizedException":
      if (where === "signin") {
        return "Email or password not accepted. Temporary passwords expire.";
      }
      // Cognito raises this both for an account still on the temporary
      // password it emailed, and for one an administrator has disabled. The
      // message text is the only thing that separates the two, and they need
      // opposite instructions, so it is read rather than ignored.
      if (/current state/i.test(message)) {
        return "This account is still on its first temporary password. Sign in with the password Cognito emailed and you will be asked to set a new one.";
      }
      return "This account cannot be reset from here. Ask James.";

    case "PasswordResetRequiredException":
      return "This account needs a new password before it can be used.";

    case "InvalidPasswordException":
      return `That password does not meet the rules: ${message}`;

    case "CodeMismatchException":
      return "That code is not right. Check the email and try again.";

    case "ExpiredCodeException":
      return "That code has expired. Ask for a new one.";

    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts on this account. Wait, then try again.";

    // InvalidParameterException is a bucket. Cognito raises it for a missing
    // parameter, a malformed code and an account with no verified address to
    // send to, and only the message tells them apart. Naming one of those as
    // the cause put "no verified email address" on screen for a request that
    // was missing a username, which is the screen asserting a cause it cannot
    // know. So the cause is claimed only where Cognito states it.
    case "InvalidParameterException":
      if (where === "reset" && /verified/i.test(message)) {
        return "This account has no verified email address, so a code cannot be sent. Ask James.";
      }
      return message || "Cognito refused that request and gave no reason.";

    case "UserNotConfirmedException":
      return "This account is not confirmed yet. Ask James.";

    case "NetworkError":
      return "Could not reach Cognito. Check your connection and try again.";

    default:
      return message || "That did not work and Cognito gave no reason.";
  }
}
